import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Animated,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import {
  AntDesign,
  Ionicons,
  MaterialIcons,
  Octicons,
} from "@expo/vector-icons";
import { v4 as uuidv4 } from "uuid";

import Colors from "../../../Resources/Color";
import Fonts from "../../../Resources/Fonts";
import IconEmptyView from "../../Components/IconEmptyView";
import FloatingMenu from "../../Components/FloatingMenu";
import HoverableTouchableOpacity from "../../Components/HoverableTouchableOpacity";
import { useToast } from "../../contexts/ToastContext";
import useChevronRotation from "../../hooks/useChevronRotation";
import { useDevice, useScreenDimensions } from "../../_layout";
import { sortNutritionRecipes } from "../../Firestore/nutrition/nutritionRecords";
import { RecipeEditorView } from "./editor/RecipeEditorView";
import {
  buildRecipeBrowserItems,
  getRecipeBrowserItemKey,
  matchesRecipeBrowserFilter,
  matchesRecipeBrowserSearch,
  sortRecipeBrowserItems,
} from "./recipeBrowser";
import { buildRecipeDuplicateDraft } from "./recipeLogic";
import {
  formatCalorieValue,
  formatMacroValue,
  getUiMacroShortLabel,
  UI_MACRO_DISPLAY_ORDER,
} from "../shared/nutritionFormatters";
import {
  getSearchEnterTarget,
  isEnterKeyEvent,
} from "../shared/searchEnterTarget";
import { NutritionLibrarySkeletonGrid } from "../shared/NutritionLibrarySkeletonGrid";
import { useDesktopAutoFocusTextInput } from "../shared/useDesktopAutoFocusTextInput";
import { getNutritionEntityDisplayName } from "../shared/nutritionEntityNameUtils";
import { useLatchedEditorPersistedState } from "../shared/useLatchedEditorPersistedState";

const RECIPE_FILTER_KEYS = [
  "all-string",
  "main-string",
  "breakfast-string",
  "snack-string",
];

const RECIPE_FILTER_CONFIG = {
  "all-string": {
    icon: "grid-outline",
  },
  "main-string": {
    icon: "restaurant-outline",
  },
  "breakfast-string": {
    icon: "cafe-outline",
  },
  "snack-string": {
    icon: "nutrition-outline",
  },
};

const RECIPE_SORT_OPTIONS = [
  {
    key: "default",
    labelKey: "default-string",
    field: null,
  },
  {
    key: "energy",
    labelKey: "energy-string",
    field: "calories",
  },
  ...UI_MACRO_DISPLAY_ORDER.map((field) => ({
    key: field,
    labelKey: `${field}-string`,
    field,
  })),
];

const RecipeCard = ({
  item,
  t,
  screenSize,
  language,
  onPress,
  isEnterTarget = false,
  onHoverStart,
}) => {
  const { isMobile } = useDevice();
  const [isHovered, setIsHovered] = useState(false);
  const recipe = item?.sourceRecord ?? null;
  const displayName = item?.name || t("recipes-string");
  const previewFoods = Array.isArray(recipe?.foods) ? recipe.foods.slice(0, 3) : [];
  const remainingCount = Math.max(
    (recipe?.foods?.length || 0) - previewFoods.length,
    0,
  );
  const macroValues = {
    protein: recipe?.protein,
    carbs: recipe?.carbs,
    fats: recipe?.fats,
  };
  const columnCount = screenSize === "small" ? 1 : 2;
  const gap = screenSize === "small" ? 8 : 12;
  const cardWidth = `calc((100% - ${(columnCount - 1) * gap}px) / ${columnCount})`;
  const recipeTypeLabel = t(item?.typeKey ?? "main-string");

  return (
    <View style={{ width: cardWidth }}>
      <TouchableOpacity
        activeOpacity={0.96}
        onPress={onPress}
        onMouseEnter={
          !isMobile
            ? () => {
                setIsHovered(true);
                onHoverStart?.();
              }
            : undefined
        }
        onMouseLeave={!isMobile ? () => setIsHovered(false) : undefined}
        style={styles.cardTouchable}
      >
        <View
          style={[
            styles.card,
            isHovered && styles.cardHovered,
            isEnterTarget && styles.cardEnterTarget,
          ]}
        >
          <View style={styles.cardContent}>
            <View style={styles.cardLeft}>
              <Text numberOfLines={1} style={styles.cardName}>
                {displayName}
              </Text>
              <View style={styles.cardMetaRow}>
                <Text numberOfLines={1} style={styles.cardDatabase}>
                  {recipeTypeLabel}
                </Text>
              </View>
              <View style={styles.previewList}>
                {previewFoods.map((foodName) => (
                  <Text
                    key={foodName}
                    numberOfLines={1}
                    style={styles.previewRowName}
                  >
                    {foodName}
                  </Text>
                ))}
                {remainingCount > 0 ? (
                  <Text numberOfLines={1} style={styles.previewOverflow}>
                    +{remainingCount}{" "}
                    {(remainingCount === 1
                      ? t("food-string")
                      : t("foods-string")
                    ).toLowerCase()}
                  </Text>
                ) : null}
              </View>
            </View>
            <View style={styles.cardRight}>
              <Text numberOfLines={1} style={styles.cardKcal}>
                {formatCalorieValue(recipe?.calories)}
                <Text style={styles.cardKcalUnit}> kcal</Text>
              </Text>
              <View style={styles.cardMacroRow}>
                {UI_MACRO_DISPLAY_ORDER.map((key) => (
                  <Text key={key} numberOfLines={1} style={styles.cardMacroValue}>
                    {getUiMacroShortLabel(key, language)}{" "}
                    <Text style={styles.cardMacroNum}>
                      {formatMacroValue(macroValues[key])}g
                    </Text>
                  </Text>
                ))}
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    </View>
  );
};

function createEmptyRecipe(order = 0) {
  return {
    id: uuidv4(),
    name: "",
    description: "",
    meal_types: ["main"],
    items: [],
    order,
  };
}

export const RecipesView = observer(({ coach }) => {
  const { i18n, t } = useTranslation();
  const { showToast } = useToast();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const { screenSize } = useScreenDimensions();
  const { isMobile, isIOS } = useDevice();
  const [selectedFilter, setSelectedFilter] = useState("all-string");
  const [selectedSortKey, setSelectedSortKey] = useState("default");
  const [isSortTriggerHovered, setIsSortTriggerHovered] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [hoveredFilterKey, setHoveredFilterKey] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [enterTargetOverrideKey, setEnterTargetOverrideKey] = useState(null);
  const [isCreateButtonHovered, setIsCreateButtonHovered] = useState(false);
  const searchInputRef = useRef(null);
  const isSearchInputPressingRef = useRef(false);
  const sortChevronRotation = useChevronRotation(
    isSortMenuOpen,
    "closedDownOpenUp",
  );
  useDesktopAutoFocusTextInput(searchInputRef, { isMobile });
  const recipes = Array.isArray(coach?.nutritionStore?.sortedRecipes)
    ? coach.nutritionStore.sortedRecipes
    : [];
  const shouldShowInitialSkeletons =
    coach?.nutritionStore?.isFetchingRecipes === true &&
    coach?.nutritionStore?.hasFetchedRecipes !== true;
  const editingRecipeId =
    String(coach?.nutritionEditingRecipeId ?? "").trim().toLowerCase() || null;
  const sortOptions = useMemo(
    () =>
      RECIPE_SORT_OPTIONS.map((option) => ({
        ...option,
        label: t(option.labelKey),
      })),
    [t],
  );
  const selectedSortOption =
    sortOptions.find((option) => option.key === selectedSortKey) ||
    sortOptions[0];
  // Keep the browser projection in observer render flow so same-entity draft field
  // updates, such as naming a newly created recipe before closing the editor, are
  // reflected immediately when returning to the list.
  const recipeBrowserItems = buildRecipeBrowserItems(sortNutritionRecipes(recipes), {
    language,
    t,
  });
  const nextRecipeOrder = useMemo(
    () =>
      recipes.length > 0
        ? Math.max(...recipes.map((recipe) => Number(recipe?.order ?? -1))) + 1
        : 0,
    [recipes],
  );
  const currentEditorRecipe = editingRecipeId
    ? coach?.nutritionStore?.recipeEntitiesById?.get?.(editingRecipeId) ?? null
    : null;
  const isEditingExistingRecipe = useLatchedEditorPersistedState(
    editingRecipeId,
    currentEditorRecipe,
  );
  const filteredRecipes = useMemo(() => {
    const matchingRecipes = recipeBrowserItems.filter((item) => {
      return (
        matchesRecipeBrowserSearch(item, searchText) &&
        matchesRecipeBrowserFilter(item, selectedFilter)
      );
    });

    return sortRecipeBrowserItems(matchingRecipes, selectedSortOption?.field);
  }, [recipeBrowserItems, searchText, selectedFilter, selectedSortOption?.field]);
  const enterTargetRecipe = useMemo(
    () =>
      getSearchEnterTarget({
        searchText,
        items: filteredRecipes,
        getItemKey: getRecipeBrowserItemKey,
        overrideKey: enterTargetOverrideKey,
      }),
    [enterTargetOverrideKey, filteredRecipes, searchText],
  );
  const hasEnterTarget = enterTargetRecipe != null;
  const enterTargetRecipeKey = getRecipeBrowserItemKey(enterTargetRecipe);

  useEffect(() => {
    if (editingRecipeId && !currentEditorRecipe) {
      coach?.setProperty?.("nutritionEditingRecipeId", null);
    }
  }, [coach, currentEditorRecipe, editingRecipeId]);

  useEffect(() => {
    setEnterTargetOverrideKey(null);
  }, [searchText]);

  const openCreateRecipe = useCallback(() => {
    const nextRecipe = coach?.nutritionStore?.createDraftRecipe?.(
      createEmptyRecipe(nextRecipeOrder),
    ) ?? createEmptyRecipe(nextRecipeOrder);
    coach?.setProperty?.("nutritionEditingRecipeId", nextRecipe.id);
  }, [coach, nextRecipeOrder]);

  const openEditRecipe = useCallback((recipe) => {
    coach?.setProperty?.("nutritionEditingRecipeId", recipe?.id ?? null);
  }, [coach]);
  const openEnterTargetRecipe = useCallback(() => {
    if (!enterTargetRecipe) {
      return;
    }

    openEditRecipe(enterTargetRecipe.sourceRecord);
  }, [enterTargetRecipe, openEditRecipe]);

  const closeRecipeEditor = useCallback(async () => {
    if (editingRecipeId) {
      try {
        await coach?.nutritionStore?.finalizeRecipeEditorClose?.(editingRecipeId);
      } catch (error) {
        console.error("Failed to finalize recipe editor close:", error);
        showToast("nutritionRecipeDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionRecipeDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        return false;
      }
    }
    coach?.setProperty?.("nutritionEditingRecipeId", null);
    return true;
  }, [coach, editingRecipeId, showToast]);
  const handleDuplicateRecipe = useCallback(() => {
    const recipeDraft = currentEditorRecipe?.getDraftSnapshot?.();
    if (!coach?.nutritionStore || !recipeDraft) {
      return;
    }

    const nextRecipe = coach.nutritionStore.createDraftRecipe(
      buildRecipeDuplicateDraft(recipeDraft, {
        language,
        order: nextRecipeOrder,
      }),
    );
    coach.nutritionStore.ensureEntityHasUnsyncedDraftForImmediateSave(nextRecipe);
    nextRecipe.saveNow?.().catch((error) => {
      console.error("Failed to queue duplicated recipe save:", error);
    });
    coach?.setProperty?.("nutritionEditingRecipeId", nextRecipe.id);
  }, [coach, currentEditorRecipe, language, nextRecipeOrder]);
  const handleDeleteRecipe = useCallback(
    async (recipeEntity) => {
      const recipeId = recipeEntity?.id ?? currentEditorRecipe?.id;
      if (!coach?.nutritionStore || !recipeId) {
        return;
      }

      const deleteRecipeName = getNutritionEntityDisplayName(
        "recipe",
        recipeEntity ?? currentEditorRecipe,
        language,
      );

      try {
        await coach.nutritionStore.deleteRecipe(recipeId);
        await closeRecipeEditor();
        showToast("nutritionRecipeDeleteSuccessTitle-string", {
          type: "info",
          title: "nutritionRecipeDeleteSuccessTitle-string",
          description: "nutritionRecipeDeleteSuccessDescription-string",
          variables: {
            name: deleteRecipeName,
          },
        });
      } catch (error) {
        console.error("Failed to delete recipe:", error);
        showToast("nutritionRecipeDeleteErrorTitle-string", {
          type: "error",
          title: "nutritionRecipeDeleteErrorTitle-string",
          description: "nutritionCommonTryAgainLater-string",
        });
        throw error;
      }
    },
    [closeRecipeEditor, coach, currentEditorRecipe, language, showToast],
  );
  const focusSearchInput = useCallback(() => {
    if (isSearchInputPressingRef.current) {
      return;
    }

    searchInputRef.current?.focus();
  }, []);
  const handleSearchKeyPress = useCallback(
    (event) => {
      if (!isEnterKeyEvent(event)) {
        return;
      }

      if (shouldShowInitialSkeletons || !hasEnterTarget) {
        return;
      }

      event?.preventDefault?.();
      openEnterTargetRecipe();
    },
    [hasEnterTarget, openEnterTargetRecipe, shouldShowInitialSkeletons],
  );

  if (currentEditorRecipe) {
    return (
      <RecipeEditorView
        coach={coach}
        recipe={currentEditorRecipe}
        isEditing={isEditingExistingRecipe}
        onBack={closeRecipeEditor}
        onDuplicate={handleDuplicateRecipe}
        onDelete={handleDeleteRecipe}
      />
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.headerCard}>
        <View style={styles.searchRow}>
          <TouchableOpacity
            activeOpacity={1}
            onPress={focusSearchInput}
            style={styles.searchField}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={t("searchByName-string")}
            aria-label={t("searchByName-string")}
          >
            <View style={styles.searchIcon}>
              <Octicons name="search" size={14} color={Colors.grayColor} />
            </View>
            <TextInput
              ref={searchInputRef}
              value={searchText}
              onChangeText={setSearchText}
              onKeyPress={handleSearchKeyPress}
              onPressIn={() => {
                isSearchInputPressingRef.current = true;
              }}
              onPressOut={() => {
                isSearchInputPressingRef.current = false;
              }}
              onBlur={() => {
                isSearchInputPressingRef.current = false;
              }}
              placeholder={t("searchByName-string")}
              placeholderTextColor={Colors.grayColor}
              style={[
                styles.searchInput,
                isMobile && isIOS ? styles.searchInputIOS : null,
              ]}
              accessibilityLabel={t("searchByName-string")}
              aria-label={t("searchByName-string")}
            />
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setIsCreateButtonHovered(false);
              openCreateRecipe();
            }}
            onMouseEnter={!isMobile ? () => setIsCreateButtonHovered(true) : undefined}
            onMouseLeave={!isMobile ? () => setIsCreateButtonHovered(false) : undefined}
          >
            <View
              style={[
                styles.createButton,
                isCreateButtonHovered ? styles.createButtonHovered : null,
              ]}
            >
              <AntDesign name="plus" size={12} color={Colors.whiteColor} />
              <Text style={styles.createButtonText}>{t("create-string")}</Text>
            </View>
          </TouchableOpacity>
        </View>
        <View style={styles.divider} />
        <View style={styles.bottomRow}>
          <View style={styles.filters}>
            {RECIPE_FILTER_KEYS.map((filterKey) => {
              const isActive = selectedFilter === filterKey;
              const isHovered = !isMobile && hoveredFilterKey === filterKey;
              const useHighlight = isActive || isHovered;
              return (
                <TouchableOpacity
                  key={filterKey}
                  activeOpacity={0.8}
                  onPress={() => setSelectedFilter(filterKey)}
                  onMouseEnter={
                    !isMobile ? () => setHoveredFilterKey(filterKey) : undefined
                  }
                  onMouseLeave={
                    !isMobile ? () => setHoveredFilterKey(null) : undefined
                  }
                  style={[
                    styles.filterItem,
                    isActive && styles.filterItemActive,
                  ]}
                >
                  <Ionicons
                    name={RECIPE_FILTER_CONFIG[filterKey].icon}
                    size={14}
                    color={
                      useHighlight ? Colors.bubbleColor : Colors.darkgrayColor
                    }
                  />
                  <Text
                    style={[
                      styles.filterText,
                      useHighlight && styles.filterTextActive,
                    ]}
                  >
                    {t(filterKey)}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
          <FloatingMenu
            placement="bottom-end"
            width={220}
            items={sortOptions}
            open={isSortMenuOpen}
            onOpenChange={setIsSortMenuOpen}
            trigger={
              <View
                style={styles.sortTrigger}
                onMouseEnter={!isMobile ? () => setIsSortTriggerHovered(true) : undefined}
                onMouseLeave={!isMobile ? () => setIsSortTriggerHovered(false) : undefined}
              >
                <MaterialIcons
                  name="sort"
                  size={14}
                  color={Colors.darkgrayColor}
                />
                {selectedSortOption?.field == null ? (
                  <Text
                    style={[
                      styles.sortTriggerLabel,
                      isSortTriggerHovered ? styles.sortTriggerLabelHovered : null,
                    ]}
                  >
                    {t("sortBy-string")}
                  </Text>
                ) : (
                  <Text style={styles.sortTriggerValue}>
                    {selectedSortOption?.label}
                  </Text>
                )}
                <Animated.View
                  style={{ transform: [{ rotate: sortChevronRotation }] }}
                >
                  <Ionicons
                    name="chevron-down"
                    size={14}
                    color={
                      isSortTriggerHovered ? Colors.darkgrayColor : Colors.grayColor
                    }
                  />
                </Animated.View>
              </View>
            }
            renderItem={({ item, close }) => (
              <HoverableTouchableOpacity
                style={styles.sortMenuItem}
                hoverStyle={styles.sortMenuItemHover}
                onPress={() => {
                  setSelectedSortKey(item.key);
                  close();
                }}
              >
                <Text
                  style={[
                    styles.sortMenuItemText,
                    item.key === selectedSortKey
                      ? styles.sortMenuItemTextActive
                      : null,
                  ]}
                >
                  {item.label}
                </Text>
                {item.key === selectedSortKey ? (
                  <Ionicons
                    name="checkmark"
                    size={15}
                    color={Colors.bubbleColor}
                  />
                ) : null}
              </HoverableTouchableOpacity>
            )}
          />
        </View>
      </View>

      <View style={styles.results}>
        {shouldShowInitialSkeletons ? (
          <NutritionLibrarySkeletonGrid
            screenSize={screenSize}
            variant="recipe"
          />
        ) : recipeBrowserItems.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="restaurant-outline"
            mainText="recipeLibraryEmptyTitle-string"
            subText="recipeLibraryEmptyDescription-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : filteredRecipes.length === 0 ? (
          <IconEmptyView
            iconLibrary="Ionicons"
            iconName="search-outline"
            mainText="noRecipesFound-string"
            subText="adjustFiltersOrSearch-string"
            marginTop={140}
            marginBottom={12}
          />
        ) : (
          <View
            style={[styles.cardGrid, { gap: screenSize === "small" ? 8 : 12 }]}
          >
            {filteredRecipes.map((recipe) => (
              <RecipeCard
                key={recipe.id}
                item={recipe}
                t={t}
                screenSize={screenSize}
                language={language}
                isEnterTarget={
                  hasEnterTarget &&
                  getRecipeBrowserItemKey(recipe) === enterTargetRecipeKey
                }
                onHoverStart={() =>
                  setEnterTargetOverrideKey(getRecipeBrowserItemKey(recipe))
                }
                onPress={() => openEditRecipe(recipe.sourceRecord)}
              />
            ))}
          </View>
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: "100%",
    marginTop: 24,
  },
  headerCard: {
    backgroundColor: Colors.whiteColor,
    borderRadius: 12,
    borderWidth: 0.5,
    borderColor: Colors.borderColor,
  },
  searchRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    minHeight: 50,
    paddingVertical: 10,
    gap: 10,
  },
  searchField: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    cursor: "text",
  },
  searchIcon: {
    justifyContent: "center",
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    marginLeft: 10,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
  },
  searchInputIOS: {
    fontSize: 16,
  },
  divider: {
    height: 0.5,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 14,
  },
  bottomRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    paddingVertical: 10,
    paddingHorizontal: 14,
    gap: 12,
  },
  filters: {
    flexDirection: "row",
    flexWrap: "wrap",
    flex: 1,
    minWidth: 0,
  },
  filterItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 10,
    marginRight: 4,
  },
  filterItemActive: {
    backgroundColor: "rgba(63, 123, 141, 0.08)",
  },
  filterText: {
    fontSize: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginLeft: 6,
  },
  filterTextActive: {
    color: Colors.bubbleColor,
  },
  createButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.bubbleColor,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 10,
    flexShrink: 0,
    transition: "opacity 180ms ease-in-out",
  },
  createButtonHovered: {
    opacity: 0.6,
  },
  createButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
    marginLeft: 6,
  },
  sortTrigger: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 8,
    flexShrink: 0,
  },
  sortTriggerLabel: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  sortTriggerLabelHovered: {
    color: Colors.darkgrayColor,
  },
  sortTriggerValue: {
    fontSize: 12,
    fontWeight: "500",
    color: Colors.darkgrayColor,
  },
  sortMenuItem: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    paddingHorizontal: 10,
    paddingVertical: 10,
    borderRadius: 8,
  },
  sortMenuItemHover: {
    backgroundColor: Colors.hoveredGray,
  },
  sortMenuItemText: {
    fontSize: 12,
    fontWeight: "400",
    color: Colors.darkgrayColor,
    flexShrink: 1,
  },
  sortMenuItemTextActive: {
    fontWeight: "500",
    color: Colors.bubbleColor,
  },
  results: {
    marginTop: 24,
    marginBottom: 36,
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  cardTouchable: {
    borderRadius: 12,
    overflow: "hidden",
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    boxShadow: "none",
  },
  card: {
    flexDirection: "row",
    alignItems: "stretch",
    justifyContent: "flex-start",
    borderRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    overflow: "hidden",
    transitionProperty: "border-color, background-color",
    transitionDuration: "200ms",
    transitionTimingFunction: "ease-in-out",
  },
  cardHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: "#FDFDFD",
  },
  cardEnterTarget: {
    borderColor: Colors.darkgrayColor,
    backgroundColor: "#FDFDFD",
  },
  cardContent: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    minWidth: 0,
    paddingVertical: 16,
    paddingLeft: 16,
    paddingRight: 18,
  },
  cardLeft: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    marginRight: 12,
  },
  cardName: {
    fontSize: 14,
    fontWeight: "500",
    lineHeight: 20,
    color: Colors.blackColor,
  },
  cardMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 4,
  },
  cardDatabase: {
    fontSize: 10,
    lineHeight: 14,
    color: Colors.grayColor,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  previewList: {
    marginTop: 6,
    gap: 2,
    height: 70,
    justifyContent: "flex-start",
  },
  previewRowName: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.darkgrayColor,
  },
  previewOverflow: {
    fontSize: 11,
    lineHeight: 16,
    color: Colors.grayColor,
    fontStyle: "italic",
    marginTop: 2,
  },
  cardRight: {
    flexGrow: 1,
    alignItems: "flex-end",
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 0,
    gap: 4,
  },
  cardKcal: {
    fontSize: 13,
    fontWeight: "600",
    color: Colors.darkgrayColor,
    flexShrink: 1,
    textAlign: "right",
    width: "100%",
  },
  cardKcalUnit: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
  cardMacroRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    flexShrink: 1,
    justifyContent: "flex-end",
    minWidth: 0,
    width: "100%",
  },
  cardMacroValue: {
    fontSize: 11,
    fontWeight: "400",
    letterSpacing: 0.2,
    color: Colors.grayColor,
    flexShrink: 1,
    textAlign: "right",
  },
  cardMacroNum: {
    fontSize: 11,
    fontWeight: "400",
    color: Colors.grayColor,
  },
});
